import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  IconArrowRight,
  IconCodeSquare,
  IconSafe,
  IconShake,
  IconThunderbolt,
} from '@arco-design/web-react/icon'
import PublicPageShell from '@/components/Public/PublicPageShell'
import { useAuthStore } from '@/stores/authStore'
import { FIXED_PUBLIC_SITE_ORIGIN } from '@/utils/commercialSite'

const FEATURE_KEYS = [
  { titleKey: 'about.feature1Title', descKey: 'about.feature1Desc' },
  { titleKey: 'about.feature2Title', descKey: 'about.feature2Desc' },
  { titleKey: 'about.feature3Title', descKey: 'about.feature3Desc' },
  { titleKey: 'about.feature4Title', descKey: 'about.feature4Desc' },
] as const

const SCENARIO_KEYS = [
  'about.scenario1',
  'about.scenario2',
  'about.scenario3',
  'about.scenario4',
] as const

export default function About() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  const features = FEATURE_KEYS.map((keys) => ({
    title: t(keys.titleKey),
    desc: t(keys.descKey),
  }))
  const scenarios = SCENARIO_KEYS.map((key) => t(key))

  const featureIcons = [
    <IconCodeSquare key="code" />,
    <IconThunderbolt key="thunder" />,
    <IconShake key="shake" />,
    <IconSafe key="safe" />,
  ]

  const handlePrimary = () => {
    if (isAuthenticated) {
      navigate('/console')
      return
    }
    navigate('/', { state: { openAuth: true, authView: 'register' } })
  }

  return (
    <PublicPageShell contentWidth="about">
      <section className="about-hero">
        <h1 className="about-hero-title">{t('about.heroTitle')}</h1>
        <p className="about-hero-desc">{t('about.heroDescription')}</p>
        <div className="about-hero-actions">
          <button type="button" className="public-btn-primary" onClick={handlePrimary}>
            {isAuthenticated ? t('common.enterConsole') : t('landing.getStarted')}
            <IconArrowRight />
          </button>
          <button type="button" className="public-btn-secondary" onClick={() => navigate('/docs')}>
            {t('landing.viewDocs')}
          </button>
          <button type="button" className="public-btn-ghost" onClick={() => navigate('/pricing')}>
            {t('about.viewPricing')}
          </button>
        </div>
      </section>

      <section>
        <h2 className="about-section-title">{t('about.featuresTitle')}</h2>
        <p className="about-section-desc">{t('about.featuresDesc')}</p>
        <div className="about-bento">
          {features.map((feature, index) => (
            <article key={feature.title} className="about-feature-card">
              <div className="about-feature-icon">{featureIcons[index]}</div>
              <h3 className="about-feature-title">{feature.title}</h3>
              <p className="about-feature-desc">{feature.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section>
        <h2 className="about-section-title">{t('about.scenariosTitle')}</h2>
        <p className="about-section-desc">{t('about.scenariosDesc')}</p>
        <div className="about-scenarios">
          {scenarios.map((scenario) => (
            <div key={scenario} className="about-scenario-item">
              {scenario}
            </div>
          ))}
        </div>
      </section>

      <section className="about-platform">
        <h2 className="about-section-title">{t('about.platformTitle')}</h2>
        <dl className="about-platform-grid mt-4">
          <div>
            <dt>{t('about.platformName')}</dt>
            <dd>TokenTCP</dd>
          </div>
          <div>
            <dt>{t('about.platformHome')}</dt>
            <dd>
              <a href={FIXED_PUBLIC_SITE_ORIGIN} target="_blank" rel="noreferrer">
                {FIXED_PUBLIC_SITE_ORIGIN}
              </a>
            </dd>
          </div>
          <div>
            <dt>{t('about.platformDocs')}</dt>
            <dd>
              <button
                type="button"
                className="public-btn-ghost p-0 h-auto min-h-0 text-[#4f46e5] hover:bg-transparent"
                onClick={() => navigate('/docs')}
              >
                {t('landing.viewDocs')}
              </button>
            </dd>
          </div>
        </dl>
      </section>
    </PublicPageShell>
  )
}
